import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { auth } from '../../../../firebase';
import axios from 'axios';
import { useDriver } from '../../../../contexts/DriverContext';

const PersonalInfoSection = ({ userData }) => {
  const { t } = useTranslation();
  const { refreshDriverAccount } = useDriver();
  const [formData, setFormData] = useState({
    name: userData?.name || '',
    email: userData?.email || '',
    phone: userData?.phone || ''
  });
  
  const [errors, setErrors] = useState({});
  const [personalInfoSubmitting, setPersonalInfoSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Update form data when userData changes
  useEffect(() => {
    setFormData({
      name: userData?.name || '',
      email: userData?.email || '',
      phone: userData?.phone || ''
    });
  }, [userData]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handlePersonalInfoSubmit = async (e) => {
    e.preventDefault();
    setPersonalInfoSubmitting(true);
    setSuccessMessage('');
    setErrors({});
    
    // Validation
    const newErrors = {};
    if (!formData.name) newErrors.name = t('settings.personalInfo.validation.nameRequired');
    if (!formData.email) newErrors.email = t('settings.personalInfo.validation.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('settings.personalInfo.validation.emailInvalid');
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setPersonalInfoSubmitting(false);
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const idToken = await auth.currentUser.getIdToken(true);
      
      const response = await axios.patch('https://api.servicegest.ro/driver/update', 
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        // Refresh driver account to get updated data
        await refreshDriverAccount();
        // Show success message
        setSuccessMessage(t('settings.personalInfo.successMessage'));
      }
    } catch (error) {
      console.error('Error updating user info:', error);
      setErrors({ submit: t('settings.personalInfo.errorMessage') });
    } finally {
      setPersonalInfoSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('settings.personalInfo.title')}
            </h3>
          </div>
        </div>
      </div>
      
      <div className="p-5">
        <form onSubmit={handlePersonalInfoSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('settings.personalInfo.fields.name')}
            </label>
            <input 
              type="text" 
              id="name" 
              name="name" 
              value={formData.name} 
              onChange={handleChange} 
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm"
              placeholder={t('settings.personalInfo.placeholders.name')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('settings.personalInfo.fields.email')}
            </label>
            <input 
              type="email" 
              id="email" 
              name="email" 
              value={formData.email} 
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm"
              placeholder={t('settings.personalInfo.placeholders.email')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.email}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('settings.personalInfo.fields.phone')}
            </label>
            <input 
              type="tel" 
              id="phone" 
              name="phone" 
              value={formData.phone} 
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm"
              placeholder={t('settings.personalInfo.placeholders.phone')}
            />
          </div>

          {errors.submit && (
            <div className="p-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm flex items-center">
              <ExclamationCircleIcon className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>{errors.submit}</span>
            </div>
          )}
          
          {successMessage && (
            <div className="p-2 rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}
          
          <div className="flex justify-end pt-2">
            <button 
              type="submit" 
              disabled={personalInfoSubmitting}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
              {personalInfoSubmitting ? t('common.saving') : t('common.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalInfoSection;
